/**
 * @fileoverview Local database error-screen recovery helpers — retry without
 *               the `__fs_update` loop param, and optional IndexedDB reset.
 * @module local-db/services/local-db-error-recovery
 *
 * Responsibilities:
 * - Strip `__fs_update` from the URL and navigate so Retry cannot immediately
 *   re-trip the 60s reload-loop guard.
 * - Delete named Dexie databases, unregister service workers, clear Cache API
 *   entries, then reload without `__fs_update` (user-initiated last resort).
 *
 * Dependencies:
 * - dexie
 * - Browser APIs: navigator.serviceWorker, caches, window.location
 *
 * Architecture:
 * - Host Database Error screens (tenant-web, system-web, mobile-pwa) must call
 *   these helpers instead of `window.location.reload()`, which keeps
 *   `__fs_update` on the URL and re-fails within 60s.
 * - Reset is destructive and local-only. Server data is not deleted; the next
 *   successful open re-syncs from the API.
 *
 * Notes:
 * - Confirm the reset with the user before deleting IndexedDB — unsynced
 *   offline writes cannot be recovered after a wipe.
 */

import Dexie from 'dexie';

/** Query param used by `triggerServiceWorkerUpdateAndReload` as a cache buster. */
export const FS_UPDATE_PARAM = '__fs_update';

type DbCloseHandler = () => Promise<void>;
const registeredCloseHandlers = new Set<DbCloseHandler>();

/**
 * @process registerDbCloseHandler
 *
 * Purpose:
 * - Register a callback to close an active Dexie singleton connection before
 *   database deletion runs during local recovery.
 *
 * Steps:
 * 1. Add handler to registeredCloseHandlers.
 * 2. Return unregister callback.
 *
 * Inputs: handler callback.
 * Outputs: unregister function.
 * Failure Handling: None.
 * Idempotency: REQUIRED.
 */
export function registerDbCloseHandler(handler: DbCloseHandler): () => void {
  registeredCloseHandlers.add(handler);
  return () => {
    registeredCloseHandlers.delete(handler);
  };
}

/**
 * @process retryWithoutUpdateParam
 *
 * Purpose:
 * - Reload the current page without the `__fs_update` loop-guard param so a
 *   user Retry is a clean navigation, not an instant re-throw.
 *
 * Steps:
 * 1. Clone the current URL.
 * 2. Delete `__fs_update`.
 * 3. `location.replace` to that URL.
 *
 * Inputs: None (browser location only).
 * Outputs: void (never returns — page navigates away).
 * Failure Handling: None — replace is best-effort.
 * Idempotency: NOT REQUIRED — navigating twice is harmless.
 */
export function retryWithoutUpdateParam(): void {
  const reloadUrl = new URL(window.location.href);
  reloadUrl.searchParams.delete(FS_UPDATE_PARAM);
  window.location.replace(reloadUrl.toString());
}

/**
 * @process resetLocalDatabaseAndReload
 *
 * Purpose:
 * - Last-resort recovery when the running bundle still cannot open IndexedDB.
 *   Deletes the named Dexie databases so the next load is a fresh `_db_meta`.
 *
 * Steps:
 * 1. Confirm with the user (unsynced local writes will be lost).
 * 2. Close/delete each named Dexie database.
 * 3. Unregister service workers and clear Cache API entries (best-effort).
 * 4. Navigate without `__fs_update`.
 *
 * Inputs:
 * - dbNames: IndexedDB database names to delete (e.g. `firesquirrel-tenant`).
 *
 * Outputs: void (never returns on confirm — page navigates away).
 *
 * Failure Handling:
 * - If any named Dexie.delete fails or times out, the error screen stays put
 *   with an explanation that other tabs may still hold the database open.
 * - SW / cache failures are logged and ignored.
 *
 * Idempotency: NOT REQUIRED.
 */
export async function resetLocalDatabaseAndReload(dbNames: readonly string[]): Promise<void> {
  const confirmed = window.confirm(
    'This deletes local data on this device and reloads the application. Unsynced offline work on this device may be lost. Continue?',
  );
  if (!confirmed) return;

  for (const handler of registeredCloseHandlers) {
    try {
      const closePromise = handler();
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Timed out closing IndexedDB before reset')), 4000);
      });
      await Promise.race([closePromise, timeoutPromise]).finally(() => {
        if (timer) clearTimeout(timer);
      });
    } catch (err) {
      console.warn('[local-db-recovery] Failed to close database before reset:', err);
    }
  }

  let deleteFailed = false;
  for (const name of dbNames) {
    try {
      const deletePromise = Dexie.delete(name);
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`Timed out deleting IndexedDB "${name}" (connection may be open)`)), 4000);
      });
      await Promise.race([deletePromise, timeoutPromise]).finally(() => {
        if (timer) clearTimeout(timer);
      });
    } catch (err) {
      deleteFailed = true;
      console.warn(`[local-db-recovery] Failed to delete IndexedDB "${name}":`, err);
    }
  }

  if (deleteFailed) {
    window.alert(
      'Local data could not be deleted. Close every other tab or window for this application, then try Reset again.',
    );
    return;
  }

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    } catch (err) {
      console.warn('[local-db-recovery] SW unregister failed (non-fatal):', err);
    }
  }

  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    } catch (err) {
      console.warn('[local-db-recovery] Cache clearing failed (non-fatal):', err);
    }
  }

  retryWithoutUpdateParam();
}
